import { Property as SsqProperty, SsqVersion } from "src/api/sure-api/ssq/useGetOneSsqVersion";
import { CalculationType } from "src/api/client-api/property/useUpdateProperty";

// // Cache pour éviter les calculs en boucle
const propertyFormulaCache = new Map<string, any[]>();

const findPropertyInSsqVersion = (ssqVersion: SsqVersion, targetId: number): SsqProperty | null => {
    const searchInHeading = (heading: any): SsqProperty | null => {
        // Search in properties of this heading using the correct API structure
        if (heading.ssqSsqHeadingSsqProperties) {
            for (const ssqSsqHeadingSsqProperty of heading.ssqSsqHeadingSsqProperties) {
                const property = ssqSsqHeadingSsqProperty.ssqProperty;
                if (property.id === targetId) {
                    return property;
                }

                // Search recursively in children of this property
                const found = findPropertyInChildren(property, targetId);
                if (found) return found;
            }
        }

        // Search in sub-headings
        if (heading.children) {
            for (const subHeading of heading.children) {
                const found = searchInHeading(subHeading);
                if (found) return found;
            }
        }

        return null;
    };

    const findPropertyInChildren = (property: SsqProperty, targetId: number): SsqProperty | null => {
        if (property.children) {
            for (const childProperty of property.children) {
                if (childProperty.id === targetId) {
                    return childProperty;
                }

                // Search recursively
                const found = findPropertyInChildren(childProperty, targetId);
                if (found) return found;
            }
        }
        return null;
    };

    // Search in all headings of the SSQ using the correct API structure
    if (ssqVersion.parentSsqSsqHeadings) {
        for (const heading of ssqVersion.parentSsqSsqHeadings) {
            const found = searchInHeading(heading);
            if (found) return found;
        }
    }

    return null;
};

/**
 * Trouve toutes les formules associées à une propriété
 */
export const findPropertyFormulas = (ssqVersion: SsqVersion, propertyId: number): any[] => {
    const cacheKey = `${ssqVersion.id}-${propertyId}`;

    // Vérifier le cache
    if (propertyFormulaCache.has(cacheKey)) {
        return propertyFormulaCache.get(cacheKey) || [];
    }

    const property = findPropertyInSsqVersion(ssqVersion, propertyId);

    if (!property || !property.formulas) {
        propertyFormulaCache.set(cacheKey, []);
        return [];
    }

    const formulas = property.formulas || [];
    propertyFormulaCache.set(cacheKey, formulas);

    return formulas;
};

/**
 * Vérifie si une propriété a des formules
 */
export const hasFormulas = (ssqVersion: SsqVersion, propertyId: number): boolean => {
    return findPropertyFormulas(ssqVersion, propertyId).length > 0;
};

/**
 * Vérifie si une propriété a des formules sans scores associés
 */
export const hasFormulasWithoutScores = (ssqVersion: SsqVersion, propertyId: number): boolean => {
    const formulas = findPropertyFormulas(ssqVersion, propertyId);
    return formulas.some((formula) => !formula.score);
};

/**
 * Détermine le type de calcul approprié pour une propriété
 */
export const determineCalculationType = (
    ssqVersion: SsqVersion,
    propertyId: number,
    isUserInput: boolean = false,
): CalculationType => {
    // Check if property has formulas without scores (formula fields)
    if (isUserInput && hasFormulasWithoutScores(ssqVersion, propertyId)) {
        return "formula";
    }

    // Check if property has formulas with scores (calculated scores)
    if (hasFormulas(ssqVersion, propertyId)) {
        return "questionnaire"; // or "batch" depending on your needs
    }

    // Par défaut, utiliser "questionnaire"
    return "questionnaire";
};

/**
 * Handle formula calculation responses and trigger target property update
 * Uses React Query cache invalidation for proper data synchronization
 */
export const handleFormulaResponse = (
    response: any,
    propertyId: number,
    updateProperty: any,
    postId: number,
    queryClient?: any,
    savingStatus?: any,
    startingPostId?: number, // Add starting post ID parameter for Action Plan context
): { isFormula: boolean; value?: any; regularData?: any } => {
    // Check if this is a formula calculation response
    if (response.success && response.calculationType === "formula" && response.result) {
        const { calculatedValue, targetPropertyId } = response.result;

        // If we have a target property different from the current property, update it
        if (targetPropertyId && targetPropertyId !== propertyId && calculatedValue !== undefined) {
            // Update the target property via API using React Query mutation
            updateProperty.mutate(
                {
                    postId: postId,
                    propertyId: targetPropertyId,
                    value: String(calculatedValue),
                    position: 0,
                    isGenericValue: null,
                    dataListOptionCodes: null,
                    calculationType: "questionnaire",
                },
                {
                    onSuccess: (targetResponse: any) => {
                        // Force React Query cache invalidation to refresh all components
                        if (queryClient) {
                            // Invalidate Action Plan queries to refresh the data
                            queryClient.invalidateQueries(["getOneActionPlanActions"]);
                            queryClient.invalidateQueries(["getOneActionPlan"]);
                            queryClient.invalidateQueries(["getAllActionPlanActions"]);

                            // Invalidate questionnaire queries (FormProperty components)
                            queryClient.invalidateQueries(["getCompanyPost"]);
                            queryClient.invalidateQueries(["getProductPost"]);
                            queryClient.invalidateQueries(["getIngredientPost"]);
                            queryClient.invalidateQueries(["getPackagingPost"]);
                            queryClient.invalidateQueries(["getOnePost"]);
                            queryClient.invalidateQueries(["getIngredientPosts"]);
                            queryClient.invalidateQueries(["getSomeIngredientPosts"]);

                            // Also invalidate property-related queries
                            queryClient.invalidateQueries(["getCompanyScoresHeadings"]);
                            queryClient.invalidateQueries(["getOneCompany"]);
                            queryClient.invalidateQueries(["getOneProduct"]);
                            queryClient.invalidateQueries(["getOneIngredient"]);
                        } else {
                            console.error("🎯 No queryClient found");
                        }
                    },
                    onError: (error: any) => {
                        // Clear loading state on error
                        if (savingStatus) {
                            savingStatus.setSaving(false);
                        }
                    },
                    onSettled: () => {
                        // Always clear loading state when target property update is complete
                        if (savingStatus) {
                            savingStatus.setSaving(false);
                        }
                    },
                },
            );
        } else {
            // If no target property update needed, clear loading state immediately
            if (savingStatus) {
                savingStatus.setSaving(false);
            }
        }

        return {
            isFormula: true,
            value: calculatedValue,
            regularData: null,
        };
    }

    // Regular calculation response
    return {
        isFormula: false,
        value: null,
        regularData: response,
    };
};
